import {
  STORY_CREATURE_CARDS,
  getStoryCreatureCard,
} from "../../../@shared/utils/storyCreatures";
import {
  STORY_CAVE_ENCOUNTER_MISSION_ID,
  STORY_CAVE_ENCOUNTER_MODE,
  STORY_CAVE_ENCOUNTER_SET_ID,
  buildStoryCaveEncounterId,
  getStoryCaveEncounterCreatureIds,
  getStoryCaveEncounterEpoch,
  hashStoryCaveEncounterValue,
  isStoryCaveEncounterDefeated,
} from "../../../@shared/utils/storyCaveEncounters";
import { STORY_WORLD_ENCOUNTER_RELEASE_EVENT } from "../../../@shared/utils/storyWorldEncounterContext";

const TILE_SIZE = 32;
const FLOOR_GID = 1025;
const COLLISION_LAYER_NAMES = ["CaveWall", "Holes", "Rocks"];
const CREATURE_FRAME_SIZE = 24;
const CREATURE_FRAMES_PER_ROW = 4;
const CREATURE_SCALE = 1.3;
const CREATURE_DEPTH = 890;
const CREATURE_ROAM_SPEED = 0.34;
const CREATURE_CHASE_SPEED = 1.16;
const CREATURE_AGGRO_RANGE = 142;
const CREATURE_GIVE_UP_RANGE = 238;
const CREATURE_TOUCH_RANGE = 34;
const CREATURE_MIN_SEPARATION = 176;
const NAVIGATION_PADDING = 96;
const PLAYER_SPAWN_PADDING = 210;
const ANIMATION_ROWS = Object.freeze({
  down: 0,
  left: 1,
  right: 2,
  up: 3,
  dead: 4,
});

const getCreatureTextureKey = (creatureId = "") =>
  `vcave-story-creature-${creatureId || "unknown"}`;

const getCreatureAnimationKey = (creatureId = "", direction = "down") =>
  `${getCreatureTextureKey(creatureId)}:${direction}`;

const createSeededRandom = (seedValue = 0) => {
  let seed = Number(seedValue) >>> 0;
  return () => {
    seed += 0x6d2b79f5;
    let value = seed;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
};

const rectanglesOverlap = (left, right) =>
  left.x < right.x + right.width &&
  left.x + left.width > right.x &&
  left.y < right.y + right.height &&
  left.y + left.height > right.y;

const getLayerData = (source, name) =>
  source?.layers?.find(
    (layer) => layer.type === "tilelayer" && layer.name === name,
  )?.data || [];

const getNavigationRects = (source) =>
  source?.layers?.find(
    (layer) => layer.type === "objectgroup" && layer.name === "Navigation",
  )?.objects || [];

const buildCaveWalkability = (source) => {
  const caveGround = getLayerData(source, "CaveGround");
  const ground = getLayerData(source, "Ground");
  const collisionLayers = COLLISION_LAYER_NAMES.map((name) =>
    getLayerData(source, name),
  );
  const navigationRects = getNavigationRects(source).map((object) => ({
    x: Number(object.x || 0),
    y: Number(object.y || 0),
    width: Number(object.width || 0),
    height: Number(object.height || 0),
  }));
  const width = Number(source?.width || 0);
  const height = Number(source?.height || 0);

  const isTileWalkable = (tileX, tileY) => {
    if (tileX < 0 || tileY < 0 || tileX >= width || tileY >= height) {
      return false;
    }
    const index = tileY * width + tileX;
    if (collisionLayers.some((layer) => Boolean(layer[index]))) return false;
    return caveGround[index] === FLOOR_GID || Boolean(ground[index]);
  };

  const isPointWalkable = (x, y) => {
    const sampleOffsets = [
      [0, 0],
      [-10, -10],
      [10, -10],
      [-10, 10],
      [10, 10],
    ];
    return sampleOffsets.every(([offsetX, offsetY]) =>
      isTileWalkable(
        Math.floor((Number(x) + offsetX) / TILE_SIZE),
        Math.floor((Number(y) + offsetY) / TILE_SIZE),
      ),
    );
  };

  const isDirectPathWalkable = (startX, startY, endX, endY) => {
    const distance = Math.hypot(endX - startX, endY - startY);
    const steps = Math.max(1, Math.ceil(distance / 12));
    for (let step = 1; step <= steps; step += 1) {
      const ratio = step / steps;
      if (
        !isPointWalkable(
          startX + (endX - startX) * ratio,
          startY + (endY - startY) * ratio,
        )
      ) {
        return false;
      }
    }
    return true;
  };

  const candidates = [];
  for (let tileY = 2; tileY < height - 2; tileY += 2) {
    for (let tileX = 2; tileX < width - 2; tileX += 2) {
      const x = tileX * TILE_SIZE + TILE_SIZE / 2;
      const y = tileY * TILE_SIZE + TILE_SIZE / 2;
      const candidateRect = {
        x: x - NAVIGATION_PADDING,
        y: y - NAVIGATION_PADDING,
        width: NAVIGATION_PADDING * 2,
        height: NAVIGATION_PADDING * 2,
      };
      if (!isPointWalkable(x, y)) continue;
      if (navigationRects.some((rect) => rectanglesOverlap(candidateRect, rect))) {
        continue;
      }
      candidates.push({ x, y });
    }
  }

  return { candidates, isPointWalkable, isDirectPathWalkable };
};

const selectEncounterHomes = ({
  candidates,
  count,
  random,
  spawnPoint,
  reservedPoints = [],
}) => {
  const shuffled = [...candidates].sort(() => random() - 0.5);
  const selected = [];
  shuffled.forEach((candidate) => {
    if (selected.length >= count) return;
    if (
      spawnPoint &&
      Math.hypot(candidate.x - spawnPoint.x, candidate.y - spawnPoint.y) <
        PLAYER_SPAWN_PADDING
    ) {
      return;
    }
    if (
      reservedPoints.some(
        (point) =>
          Math.hypot(candidate.x - Number(point.x), candidate.y - Number(point.y)) <
          NAVIGATION_PADDING,
      )
    ) {
      return;
    }
    if (
      selected.some(
        (point) =>
          Math.hypot(candidate.x - point.x, candidate.y - point.y) <
          CREATURE_MIN_SEPARATION,
      )
    ) {
      return;
    }
    selected.push(candidate);
  });
  return selected;
};

export const buildCaveCreatureEncounterHomes = ({
  source,
  sceneKey = "VoidCaveScene",
  epoch = getStoryCaveEncounterEpoch(),
  spawnPoint = null,
  encounterCount = null,
  reservedPoints = [],
} = {}) => {
  const seed = hashStoryCaveEncounterValue(`${sceneKey}:${epoch}`);
  const random = createSeededRandom(seed);
  const walkability = buildCaveWalkability(source);
  const requestedCount =
    encounterCount !== null && Number.isFinite(Number(encounterCount))
    ? Number(encounterCount)
    : Math.max(2, Math.min(5, Math.round(walkability.candidates.length / 55)));

  return {
    epoch,
    homes: selectEncounterHomes({
      candidates: walkability.candidates,
      count: requestedCount,
      random,
      spawnPoint,
      reservedPoints,
    }),
    random,
    walkability,
  };
};

export const preloadCaveCreatureEncounters = (scene) => {
  STORY_CREATURE_CARDS.forEach((creature) => {
    if (!creature?.worldImage) return;
    const textureKey = getCreatureTextureKey(creature.id);
    if (scene.textures.exists(textureKey)) return;
    scene.load.spritesheet(textureKey, creature.worldImage, {
      frameWidth: CREATURE_FRAME_SIZE,
      frameHeight: CREATURE_FRAME_SIZE,
    });
  });
};

const ensureCreatureAnimations = (scene, creatureId = "") => {
  const normalizedCreatureId = String(creatureId || "").trim().toLowerCase();
  const textureKey = getCreatureTextureKey(normalizedCreatureId);
  if (!normalizedCreatureId || !scene.textures.exists(textureKey)) return;

  Object.entries(ANIMATION_ROWS).forEach(([direction, rowIndex]) => {
    const animationKey = getCreatureAnimationKey(normalizedCreatureId, direction);
    if (scene.anims.exists(animationKey)) return;
    const start = rowIndex * CREATURE_FRAMES_PER_ROW;
    scene.anims.create({
      key: animationKey,
      frames: scene.anims.generateFrameNumbers(textureKey, {
        start,
        end: start + CREATURE_FRAMES_PER_ROW - 1,
      }),
      frameRate: 7,
      repeat: direction === "dead" ? 0 : -1,
    });
  });
};

const setCreatureAnimation = (entry, direction = "down", moving = false) => {
  if (!entry?.sprite || !entry?.creature?.id) return;
  const nextDirection = Object.prototype.hasOwnProperty.call(
    ANIMATION_ROWS,
    direction,
  )
    ? direction
    : "down";
  entry.lastDirection = nextDirection;
  if (moving) {
    entry.sprite.play(
      getCreatureAnimationKey(entry.creature.id, nextDirection),
      true,
    );
    return;
  }
  entry.sprite.anims.stop();
  entry.sprite.setFrame(ANIMATION_ROWS[nextDirection] * CREATURE_FRAMES_PER_ROW);
};

const setEncounterVisible = (entry, visible) => {
  entry.hidden = !visible;
  entry.sprite.setVisible(visible);
  entry.shadow.setVisible(visible);
  entry.badgeBg.setVisible(visible);
  entry.badgeText.setVisible(visible);
};

export const createCaveCreatureEncounters = (
  scene,
  {
    source,
    storyMapId = "voidCave",
    encounterCount = null,
    reservedPoints = [],
  } = {},
) => {
  destroyCaveCreatureEncounters(scene);
  if (scene.userData?.activeGameMode !== "story" || !source) return;

  const { epoch, homes, random, walkability } =
    buildCaveCreatureEncounterHomes({
      source,
      sceneKey: scene.scene.key,
      encounterCount,
      spawnPoint: scene.spawnPoint,
      reservedPoints,
    });

  scene.caveCreatureEncounterState = {
    epoch,
    storyMapId,
    entries: new Map(),
    launchId: "",
    walkability,
    random,
  };

  homes.forEach((home, slot) => {
    const encounterId = buildStoryCaveEncounterId({
      sceneKey: scene.scene.key,
      epoch,
      slot,
    });
    const creatureIds = getStoryCaveEncounterCreatureIds(encounterId);
    const creature = getStoryCreatureCard(creatureIds[0]);
    const textureKey = getCreatureTextureKey(creature?.id);
    if (!creature || !scene.textures.exists(textureKey)) return;
    ensureCreatureAnimations(scene, creature.id);
    const creatureDepth = scene.getGroundEntityDepthAt?.(home.y + 8) ||
      CREATURE_DEPTH;

    const shadow = scene.add
      .ellipse(home.x, home.y + 14, 34, 12, 0x0b0610, 0.34)
      .setDepth(creatureDepth - 1);
    const sprite = scene.add
      .sprite(home.x, home.y, textureKey, 0)
      .setScale(CREATURE_SCALE)
      .setDepth(creatureDepth);
    const badgeBg = scene.add.graphics().setDepth(3100);
    const badgeText = scene.add
      .text(0, 0, `x${creatureIds.length}`, {
        font: "bold 14px Arial",
        fill: "#f7efff",
        stroke: "#170b22",
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(3101);
    const entry = {
      encounterId,
      creatureIds,
      creature,
      sprite,
      shadow,
      badgeBg,
      badgeText,
      homeX: home.x,
      homeY: home.y,
      x: home.x,
      y: home.y,
      targetX: home.x,
      targetY: home.y,
      state: "idle",
      waitUntil: scene.time.now + 600 + Math.round(random() * 1200),
      lastDirection: "down",
      hidden: false,
    };
    setCreatureAnimation(entry, "down", false);
    setEncounterVisible(entry, !isStoryCaveEncounterDefeated(encounterId));
    scene.caveCreatureEncounterState.entries.set(encounterId, entry);
  });

  scene._onCaveEncounterRelease = (event) => {
    const encounterId = String(event?.detail?.encounterId || "");
    const state = scene.caveCreatureEncounterState;
    if (!state?.entries.has(encounterId)) return;
    state.launchId = "";
    const entry = state.entries.get(encounterId);
    if (isStoryCaveEncounterDefeated(encounterId)) {
      setEncounterVisible(entry, false);
      return;
    }
    entry.state = "returning";
  };
  window.addEventListener(
    STORY_WORLD_ENCOUNTER_RELEASE_EVENT,
    scene._onCaveEncounterRelease,
  );
};

const chooseRoamTarget = (state, entry) => {
  const nearby = state.walkability.candidates.filter(
    (candidate) =>
      Math.hypot(candidate.x - entry.homeX, candidate.y - entry.homeY) <= 112 &&
      state.walkability.isDirectPathWalkable(
        entry.x,
        entry.y,
        candidate.x,
        candidate.y,
      ),
  );
  if (!nearby.length) return { x: entry.homeX, y: entry.homeY };
  return nearby[Math.floor(state.random() * nearby.length)];
};

const syncEncounterDisplay = (scene, entry) => {
  const creatureDepth = scene.getGroundEntityDepthAt?.(entry.y + 8) ||
    CREATURE_DEPTH;
  entry.sprite.setPosition(entry.x, entry.y).setDepth(creatureDepth);
  entry.shadow.setPosition(entry.x, entry.y + 14).setDepth(creatureDepth - 1);
  entry.badgeText.setPosition(entry.x, entry.y - 34);
  entry.badgeBg.clear();
  entry.badgeBg
    .fillStyle(0x170b22, 0.78)
    .fillRoundedRect(entry.x - 16, entry.y - 48, 32, 20, 8)
    .lineStyle(2, 0xcfb8ff, 0.92)
    .strokeRoundedRect(entry.x - 16, entry.y - 48, 32, 20, 8);
};

export const updateCaveCreatureEncounters = (scene) => {
  const state = scene.caveCreatureEncounterState;
  if (!state || !scene.player) return;
  if (
    typeof window !== "undefined" &&
    (window.__storyWorldEncounterPaused || window.__storySceneInputLocked)
  ) {
    return;
  }

  let paused = false;
  state.entries.forEach((entry) => {
    if (paused || entry.hidden) return;
    const distanceToPlayer = Math.hypot(
      scene.player.x - entry.x,
      scene.player.y - entry.y,
    );

    if (
      state.launchId !== entry.encounterId &&
      distanceToPlayer <= CREATURE_TOUCH_RANGE
    ) {
      const world = {
        mapId: state.storyMapId,
        building: "",
        spawnId: entry.encounterId,
        x: scene.player.x,
        y: scene.player.y,
        facingDirection: scene.lastFacingDirection ?? null,
      };
      const detail = {
        missionId: STORY_CAVE_ENCOUNTER_MISSION_ID,
        encounterMode: STORY_CAVE_ENCOUNTER_MODE,
        encounterSetId: STORY_CAVE_ENCOUNTER_SET_ID,
        encounterId: entry.encounterId,
        sceneKey: scene.scene.key,
        world,
        playerWorld: world,
      };
      state.launchId = entry.encounterId;
      window.__storyWorldEncounterPaused = true;
      window.__pendingStoryWorldEncounter = detail;
      window.dispatchEvent(
        new CustomEvent("story-world-encounter", { detail }),
      );
      paused = true;
      return;
    }

    const canSeePlayer = state.walkability.isDirectPathWalkable(
      entry.x,
      entry.y,
      scene.player.x,
      scene.player.y,
    );
    if (
      entry.state !== "chasing" &&
      distanceToPlayer <= CREATURE_AGGRO_RANGE &&
      canSeePlayer
    ) {
      entry.state = "chasing";
    } else if (
      entry.state === "chasing" &&
      (distanceToPlayer >= CREATURE_GIVE_UP_RANGE || !canSeePlayer)
    ) {
      entry.state = "returning";
    }

    if (entry.state === "idle" && scene.time.now >= entry.waitUntil) {
      const target = chooseRoamTarget(state, entry);
      entry.targetX = target.x;
      entry.targetY = target.y;
      entry.state = "roaming";
    }

    let targetX = entry.targetX;
    let targetY = entry.targetY;
    let speed = CREATURE_ROAM_SPEED;
    if (entry.state === "chasing") {
      targetX = scene.player.x;
      targetY = scene.player.y;
      speed = CREATURE_CHASE_SPEED;
    } else if (entry.state === "returning") {
      targetX = entry.homeX;
      targetY = entry.homeY;
      speed = CREATURE_CHASE_SPEED * 0.8;
    }

    const dx = targetX - entry.x;
    const dy = targetY - entry.y;
    const distance = Math.hypot(dx, dy);
    let moving = false;
    let direction = entry.lastDirection;
    if (distance <= speed + 0.3) {
      entry.x = targetX;
      entry.y = targetY;
      entry.state = "idle";
      entry.waitUntil = scene.time.now + 700 + Math.round(state.random() * 900);
    } else if (distance > 0) {
      const nextX = entry.x + (dx / distance) * speed;
      const nextY = entry.y + (dy / distance) * speed;
      if (state.walkability.isPointWalkable(nextX, nextY)) {
        entry.x = nextX;
        entry.y = nextY;
        moving = true;
        direction =
          Math.abs(dy) > Math.abs(dx)
            ? dy < 0
              ? "up"
              : "down"
            : dx < 0
              ? "left"
              : "right";
      } else {
        entry.state = "returning";
      }
    }

    setCreatureAnimation(entry, direction, moving);
    syncEncounterDisplay(scene, entry);
  });
};

export const destroyCaveCreatureEncounters = (scene) => {
  if (scene?._onCaveEncounterRelease && typeof window !== "undefined") {
    window.removeEventListener(
      STORY_WORLD_ENCOUNTER_RELEASE_EVENT,
      scene._onCaveEncounterRelease,
    );
  }
  scene._onCaveEncounterRelease = null;
  scene?.caveCreatureEncounterState?.entries?.forEach((entry) => {
    entry.sprite?.destroy?.();
    entry.shadow?.destroy?.();
    entry.badgeBg?.destroy?.();
    entry.badgeText?.destroy?.();
  });
  scene.caveCreatureEncounterState = null;
};
